import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { config } from "../../../../config";
import {
  ArrowLeft,
  ArrowRight,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  MessageSquare,
} from "lucide-react";
import {
  getPlatformColorClass,
  getPlatformDisplayName,
} from "../../../../utils/Convert";
import { Alert } from "@mui/material";
import AdInsightsService from "../../../../services/AdInsights";
import AIDocumentQA from "../../../../components/AIDocumentQA";
import SendFeedbackDialog from "../../../../components/SendFeedbackDialog";
import { useSelector } from "react-redux";

interface Pattern {
  description: string;
  improvement: string;
  confidence: number;
}

interface PlatformSpecificData {
  platform: string;
  patterns: Pattern[];
}

interface CrossPlatformPattern {
  platforms: string[];
  pattern: string;
  impact: string;
  confidence: number;
}

interface BudgetAdjustment {
  fromPlatform: string;
  toPlatform: string;
  percentage: number;
  reason: string;
}

export const PlatformInsight = ({
  platform,
  patterns,
}: PlatformSpecificData) => {
  const displayName = getPlatformDisplayName(platform);
  const colorClass = getPlatformColorClass(platform);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6 hover:shadow-md transition-all duration-300">
      <div className="p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${colorClass.replace(
                "bg-opacity-10",
                "bg-opacity-100"
              )}`}
            ></div>
            {displayName}
          </h3>
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}
          >
            {patterns.length} {patterns.length === 1 ? "Pattern" : "Patterns"}
          </div>
        </div>

        <div className="space-y-6">
          {patterns.map((pattern, index) => (
            <div
              key={index}
              className={`${index > 0 ? "border-t border-gray-100 pt-6" : ""}`}
            >
              <div className="flex justify-between items-start gap-4 mb-4">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-sm">
                      Finding {index + 1}
                    </span>
                  </h4>
                  <p className="text-gray-700">{pattern.description}</p>
                </div>
                <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5" />
                  {Math.round(pattern.confidence * 100)}% confidence
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-blue-50/50 rounded-xl p-5 mt-4 border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 bg-white p-1.5 rounded-full border border-blue-200">
                    <ArrowRight className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2">
                      Recommended Action
                    </h5>
                    <p className="text-gray-700">{pattern.improvement}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CrossPlatformPatterns = ({
  patterns,
}: {
  patterns: CrossPlatformPattern[];
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6 hover:shadow-md transition-all duration-300">
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-red-600" />
          Cross-Platform Insights
        </h3>

        <div className="space-y-6">
          {patterns.map((pattern, index) => (
            <div
              key={index}
              className={`${index > 0 ? "border-t border-gray-100 pt-6" : ""}`}
            >
              <div className="flex flex-wrap gap-2 mb-4">
                {pattern.platforms.map((platform) => (
                  <span
                    key={platform}
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getPlatformColorClass(
                      platform
                    )}`}
                  >
                    {getPlatformDisplayName(platform)}
                  </span>
                ))}
              </div>

              <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-gray-900 font-medium">{pattern.pattern}</p>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5" />
                  {Math.round(pattern.confidence * 100)}% confidence
                </div>
                <div className="flex items-start gap-2 bg-blue-50 px-3 py-1 rounded-full">
                  <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-blue-700 text-sm font-medium">
                    {pattern.impact}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const BudgetRecommendations = ({
  adjustments,
}: {
  adjustments: BudgetAdjustment[];
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6 hover:shadow-md transition-all duration-300">
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <span className="text-red-600 bg-red-50 p-1.5 rounded-full">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Budget Optimization Recommendations
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {adjustments.map((adjustment, index) => (
            <div
              key={index}
              className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 border border-gray-200 hover:shadow-sm transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-lg ${getPlatformColorClass(
                      adjustment.fromPlatform
                    )}`}
                  >
                    {getPlatformDisplayName(adjustment.fromPlatform)}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-600" />
                  <span
                    className={`px-3 py-1 rounded-lg ${getPlatformColorClass(
                      adjustment.toPlatform
                    )}`}
                  >
                    {getPlatformDisplayName(adjustment.toPlatform)}
                  </span>
                </div>
                <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                  {adjustment.percentage}% shift
                </div>
              </div>

              <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-100">
                <div>
                  <p className="text-sm text-gray-700">{adjustment.reason}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AdInsightsDetailView = () => {
  const navigate = useNavigate();
  const reduxUser = useSelector((state: any) => state.user);
  const { projectId, insightsId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [insight, setInsight] = useState<any>(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  const newAdInsightsServiceInstance = useMemo(
    () => new AdInsightsService(),
    []
  );

  const fetchInsightDetails = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      if (!config.inProd) {
        setInsight(config.mocks.insights[0]);
        return;
      }

      const adInsightsService = newAdInsightsServiceInstance;
      const response = await adInsightsService.fetchInsight(
        projectId,
        insightsId
      );

      setInsight(response);
    } catch (error: any) {
      console.error("Error fetching insight details:", error);
      setError(error.message || "Failed to load insight details");
    } finally {
      setLoading(false);
    }
  }, [projectId, insightsId]);

  useEffect(() => {
    fetchInsightDetails();
  }, [fetchInsightDetails]);

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="flex gap-4 items-center">
              <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
              <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            </div>
            <div className="h-60 bg-gray-200 rounded-xl"></div>
            <div className="h-60 bg-gray-200 rounded-xl"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="h-40 bg-gray-200 rounded-xl"></div>
              <div className="h-40 bg-gray-200 rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!insight) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center">
            <div className="inline-flex items-center justify-center bg-red-50 h-20 w-20 rounded-full mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No insight found
            </h3>
            <p className="mt-1 text-gray-600 mb-6">
              The requested insight could not be found.
            </p>
            <button
              onClick={() => navigate(`/project/${projectId}/adinsights`)}
              className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 font-medium flex items-center justify-center gap-2 mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Insights
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between mb-6 gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/project/${projectId}/adinsights`)}
                className="p-2 bg-white hover:bg-gray-50 rounded-xl transition-colors shadow-sm border border-gray-200"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {insight.name || "Insight Details"}
                </h1>
                <p className="text-sm text-gray-500">
                  Generated on{" "}
                  {new Date(insight.createdAt || Date.now()).toLocaleDateString(
                    "en-US",
                    {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    }
                  )}
                </p>
              </div>
            </div>
            <div className="w-full sm:w-auto flex gap-3 items-center break-words">
              <button
                onClick={() => setShowFeedbackDialog(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg shadow-sm border border-gray-200 transition-all duration-200"
              >
                <MessageSquare className="w-4 h-4" />
                Feedback
              </button>
              <AIDocumentQA
                userId={reduxUser.id}
                documentName={`Insight ${insightsId}`}
                projectId={projectId}
                documentId={insightsId}
                documentType="Insight"
                service={newAdInsightsServiceInstance}
              />
            </div>
          </div>

          {error && (
            <Alert
              severity="error"
              className="mb-6 rounded-xl"
              sx={{
                border: "1px solid rgba(211, 47, 47, 0.2)",
                borderRadius: "12px",
              }}
            >
              {error}
            </Alert>
          )}

          <div className="bg-gradient-to-r from-gray-50 to-white p-4 rounded-xl mb-6 border border-gray-200 shadow-sm">
            <div className="flex flex-wrap gap-4">
              <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center gap-3 shadow-sm">
                <div className="bg-blue-50 text-blue-700 p-2 rounded-full">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16 8V16M12 11V16M8 14V16M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Patterns</p>
                  <p className="font-semibold">
                    {insight.insights.patterns.platformSpecific.reduce(
                      (sum: number, platform: any) =>
                        sum + platform.patterns.length,
                      0
                    )}
                  </p>
                </div>
              </div>

              <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center gap-3 shadow-sm">
                <div className="bg-green-50 text-green-700 p-2 rounded-full">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 8V12L15 15M3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Platforms</p>
                  <p className="font-semibold">
                    {insight.insights.patterns.platformSpecific.length}
                  </p>
                </div>
              </div>

              <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center gap-3 shadow-sm">
                <div className="bg-amber-50 text-amber-700 p-2 rounded-full">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Budget Adjustments</p>
                  <p className="font-semibold">
                    {insight.insights.recommendations.budgetAdjustments
                      ?.length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Platform-Specific Insights */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 px-1">
              Platform Insights
            </h2>
            {insight.insights.patterns.platformSpecific.map(
              (platformData: any, index: number) => (
                <PlatformInsight
                  key={index}
                  platform={platformData.platform}
                  patterns={platformData.patterns}
                />
              )
            )}
          </div>

          {/* Cross-Platform Patterns */}
          {insight.insights.patterns.crossPlatform &&
            insight.insights.patterns.crossPlatform.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 px-1">
                  Cross-Platform Analysis
                </h2>
                <CrossPlatformPatterns
                  patterns={insight.insights.patterns.crossPlatform}
                />
              </div>
            )}

          {/* Budget Recommendations */}
          {insight.insights.recommendations.budgetAdjustments &&
            insight.insights.recommendations.budgetAdjustments.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 px-1">
                  Budget Optimization
                </h2>
                <BudgetRecommendations
                  adjustments={
                    insight.insights.recommendations.budgetAdjustments
                  }
                />
              </div>
            )}
        </div>
      </div>
      <SendFeedbackDialog
        open={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        userId={reduxUser.id}
        projectId={projectId}
        documentId={insightsId}
        documentType="adinsights"
        documentName={insight?.name || "Ad Insights"}
      />
    </>
  );
};

export default AdInsightsDetailView;
